// Pure event-shaping helpers for `sentry.server.config.ts`'s `beforeSend`.
// They live here rather than in the config because importing that module runs
// `Sentry.init()` as a side effect, which makes it untestable.

import type { ErrorEvent } from "@sentry/nextjs"

/**
 * Patterns we deliberately drop server-side before sending to Sentry.
 *
 * Each pattern targets a console.error that the runtime catches and recovers
 * from — the page still renders successfully — so the event has no actionable
 * signal but burns quota. We drop ONLY when the event came through
 * captureConsoleIntegration.
 *
 * Verified non-impacting before adding here:
 * - Stale Turbopack chunks: client triggers a hard reload via Next's
 *   ChunkLoadError handling; transactions on affected routes show 0 5xx.
 */
const DROPPED_CONSOLE_MESSAGE_PATTERNS: RegExp[] = [
  // Turbopack stale-chunk after a deploy — Next handles this with a hard reload.
  /Module \d+ was instantiated because it was required from module \d+, but the module factory is not available/,
  // Next.js App Router RSC streaming interruption — fired to console.error when
  // a soft navigation closes the SSR/RSC streaming connection before it finishes
  // (e.g. same-page router navigation, user aborts). React error #412 appears in
  // breadcrumbs. The page has already rendered; the error is handled and has no
  // stack trace. 0 users impacted (MARKETING-SITE-17D, 2 events, 2026-09-27).
  /^Connection closed\.$/,
  // GTM custom tag polling for the _ga cookie on users with GA blocked/no consent.
  // The tag retries 3 times and calls console.error when it gives up. 0 users impacted;
  // the page renders normally.
  /^GA ID not found after \d+ attempts\./,
  // A bundled third-party library tries to assign to a V8-internal Error's
  // .stack property, which V8 defines without a setter in strict-mode bundles.
  // The throw is caught by captureConsoleIntegration; the page renders fine.
  // (MARKETING-SITE-17G — 1 event, 0 users affected)
  /Cannot assign to read only property 'stack' of object ''/,
]

/**
 * `captureConsoleIntegration` renamed its mechanism between SDK majors: v8 set
 * `console`, v9+ sets `auto.core.capture_console` (see
 * `@sentry/core/build/*\/integrations/captureconsole.js`). The check only knew
 * the v8 name, so from the v10 bump until this fix `isDroppedConsoleNoise`
 * returned false for every event and the Turbopack drop above was silently
 * dead. Accept both names so an SDK bump can't disarm the filter again.
 */
const CONSOLE_MECHANISMS = new Set(["console", "auto.core.capture_console"])

/**
 * `throwOnCmsFailure` (lib/cms-server.ts) throws when a CMS fetch failed, so
 * Next never caches a "not found" shell built from an empty response. That
 * throw is correct and stays — but it is reported in EVERY environment, while
 * the diagnostic report it points at ("CMS fetch failed: <tag>", carrying the
 * path and HTTP status) is production-only: #548 deliberately suppressed CMS
 * warnings outside production, and the throw landed two days later without the
 * matching gate. Outside production that leaves a pointer with no target — an
 * error reading "See Sentry for the underlying error" when no such event was
 * ever sent. That inversion is the whole of MARKETING-SITE-101: 1278 of its
 * 1363 events are preview deploys.
 */
// Positional rather than named groups: the repo's tsconfig target predates
// ES2018, so `(?<name>…)` is a compile error here.
export const CMS_FETCH_WRAPPER =
  /^CMS fetch failed for ([^/]+)\/(.*?) — refusing to cache/

function consoleMessage(event: ErrorEvent): string | null {
  const mechanism = event.exception?.values?.[0]?.mechanism?.type
  if (!mechanism || !CONSOLE_MECHANISMS.has(mechanism)) return null
  return event.exception?.values?.[0]?.value ?? ""
}

/**
 * Drop the wrapper wherever its underlying report is suppressed, so the two
 * agree. Production still reports it — there a CMS failure is real.
 */
export function isDroppedConsoleNoise(
  event: ErrorEvent,
  vercelEnv: string | undefined
): boolean {
  const message = consoleMessage(event)
  if (message === null) return false
  if (vercelEnv !== "production" && CMS_FETCH_WRAPPER.test(message)) return true
  return DROPPED_CONSOLE_MESSAGE_PATTERNS.some((re) => re.test(message))
}

/**
 * The wrapper embeds the slug in its message, so Sentry fingerprints one issue
 * per slug — a single CMS outage fans out into `biomarkers/uric-acid`,
 * `authors/nick-lenten`, `faq-categories/our-scans-and-tests`, … (~20 issues in
 * the backlog for what is one fault). Group by collection instead and keep the
 * slug as context, so the next outage is one issue with N events.
 */
export function groupCmsFetchFailures(event: ErrorEvent): ErrorEvent {
  const message = event.exception?.values?.[0]?.value ?? ""
  const match = CMS_FETCH_WRAPPER.exec(message)
  if (!match) return event
  const [, collection, slug] = match
  event.fingerprint = ["cms-fetch-failed", collection]
  event.extra = { ...event.extra, cmsCollection: collection, cmsSlug: slug }
  return event
}

/**
 * Browser-extension content scripts inject their own webpack bundle into our
 * page and throw from it — most often an unhandled promise rejection fired
 * after the extension's runtime context is torn down. `@sentry/nextjs`'s
 * rewriteFrames integration rewrites every frame's `filename`, stripping the
 * `chrome-extension://<id>` / `moz-extension://<id>` origin down to `app:///`.
 * By the time `beforeSend` runs the origin is already gone, so the
 * `chrome-extension://` `ignoreErrors` filter can never match these. None of
 * the bundle names below are marketing-site code — our client chunks live under
 * `_next/static/chunks/` — so dropping any event whose stack touches one removes
 * the noise without masking a real app error.
 *
 * Observed injectors:
 * - `injectedScript.bundle.js` — password managers, crypto wallets (MetaMask /
 *   Phantom): `Cannot read properties of undefined (reading 'sendMessage')`
 *   (Sentry #126283416).
 * - `content-main.js` — generic extension content-script entrypoint.
 * - `static/toolbar.js` — Vercel Toolbar extension: `n.target?.matches is not a
 *   function` (Sentry #128429098).
 * - `executors/<n>.js` — extension webpack chunk, property-mangled so the
 *   missing member reads as e.g. `M_ID`; `onunhandledrejection`
 *   (MARKETING-SITE-152). Seen on Edge, single-session bursts, 0 users impacted.
 */
const EXTENSION_INJECTED_BUNDLES: RegExp[] = [
  /injectedScript\.bundle\.js/,
  /content-main\.js/,
  /static\/toolbar\.js/,
  /(?:^|\/)executors\/[^/]+\.js/,
]

export function isExtensionInjectedFrame(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      EXTENSION_INJECTED_BUNDLES.some((re) => re.test(frame.filename ?? ""))
    )
  )
}

/**
 * The Ella chat widget embed (`embed_script.js`, served from
 * chat.aheadhealth.com) throws internally when its event-listener handler
 * receives an event with an undefined value and reads `.type` off it:
 * `TypeError: Cannot read properties of undefined (reading 'type')`
 * (MARKETING-SITE-176, 39 events, 0 users affected). The throw site is inside
 * the third-party bundle — reported as `app:///embed_script.js` after
 * `@sentry/nextjs`'s rewriteFrames strips the origin — so there is nothing to
 * fix on our side. Our own client chunks live under `_next/static/chunks/`,
 * so dropping any event whose stack touches the bundle masks nothing real.
 * Frames-only, like `isExtensionInjectedFrame`: `culprit` is computed
 * server-side, so it is never present on the client-side event `beforeSend`
 * sees (and the v10 SDK type no longer carries it).
 */
const ELLA_EMBED_SCRIPT = /(?:^|\/)embed_script\.js/

export function isEllaEmbedNoise(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      ELLA_EMBED_SCRIPT.test(frame.filename ?? "")
    )
  )
}

/**
 * Google Translate injects its own JavaScript (`translate_http`) when a user views
 * the site through the translate.goog proxy. Those scripts throw internally — the
 * canonical example is `Error: La` (a minified error), caught by our global
 * onunhandledrejection handler (MARKETING-SITE-17H, 1 event, 0 users impacted).
 * Every frame in the stack is Google's code; none is ours. Our own client chunks
 * live under `_next/static/chunks/`, so dropping any event whose stack
 * touches `translate_http` scripts masks nothing real.
 * Frames-only check, same pattern as isExtensionInjectedFrame and isEllaEmbedNoise.
 */
const GOOGLE_TRANSLATE_BUNDLE = /(?:^|\/)translate_http\//

export function isGoogleTranslateNoise(event: ErrorEvent): boolean {
  const values = event.exception?.values
  if (!values) return false
  return values.some((value) =>
    value.stacktrace?.frames?.some((frame) =>
      GOOGLE_TRANSLATE_BUNDLE.test(frame.filename ?? "")
    )
  )
}

/**
 * The global-handlers integration names its unhandled-rejection mechanism
 * `auto.browser.global_handlers.onunhandledrejection` — that is the raw
 * `latestEvent.entries.exception.data.values[0].mechanism.type` on the
 * observed MARKETING-SITE-17E event. The bare `onunhandledrejection` shorthand
 * is kept so the filter still matches if an SDK emits that instead. Both names
 * are exact: the sibling `auto.browser.global_handlers.onerror` must never
 * match, so this stays a Set like `CONSOLE_MECHANISMS`, not a suffix check.
 */
const UNHANDLED_REJECTION_MECHANISMS = new Set([
  "onunhandledrejection",
  "auto.browser.global_handlers.onunhandledrejection",
])

/**
 * Third-party inline/eval'd code (GTM container tags, in-app-browser injected
 * bridges such as the Google Search App WebView shim) executes with the
 * document as its script URL, so its frames report as `app:///<page-path>`
 * with no `/_next/` frame anywhere in the stack. When such a rejection also
 * carries a very short alphabetic message (1–4 chars, e.g. `Error: La`,
 * MARKETING-SITE-17E: GSA 439.4/iOS, 1 event, 0 users), there is nothing
 * actionable on our side: our bundles always appear as
 * `app:///_next/static/chunks/*`, and every first-party inline script on the
 * page is either try/caught or promise-free. The conjunction (short message AND
 * zero chunk frames) makes this tight — either signal alone would be too broad.
 */
export function isInlineShortRejectionNoise(event: ErrorEvent): boolean {
  const value = event.exception?.values?.[0]
  if (value?.type !== "Error") return false
  if (!/^[A-Za-z]{1,4}$/.test(value.value ?? "")) return false
  const mechanism = value.mechanism?.type
  if (!mechanism || !UNHANDLED_REJECTION_MECHANISMS.has(mechanism)) return false
  // null stacktrace: no frames were captured at all — no /_next/ frames by
  // definition, so the conjunction still holds (MARKETING-SITE-17Y: Chrome iOS, iPad).
  if (!value.stacktrace) return true
  const frames = value.stacktrace.frames ?? []
  if (frames.length === 0) return false
  return !frames.some((f) => (f.filename ?? "").includes("/_next/"))
}

/**
 * The crawler/bot classes covered by Sentry's built-in inbound "filter known
 * crawlers/spiders" rule, reproduced here because the marketing-site Sentry
 * plan has no inbound filters configured — see the module-level fix
 * rationale in the 404-beacon / bot-noise change. Case-insensitive; matched
 * against a raw User-Agent string.
 */
const BOT_USER_AGENT_PATTERN =
  /bot|crawl|spider|slurp|bingpreview|facebookexternalhit|HeadlessChrome|phantomjs|Lighthouse|Chrome-Lighthouse|Google Page Speed|GTmetrix|pingdom|uptimerobot|datadog|SemrushBot|AhrefsBot|python-requests|curl|wget|Googlebot|bingbot|Applebot|YandexBot|Baiduspider|Sogou|Bytespider/i

export function isBotUserAgent(ua: string): boolean {
  if (!ua) return false
  return BOT_USER_AGENT_PATTERN.test(ua)
}

/**
 * Server-side equivalent of the client `navigator.userAgent` check in
 * `instrumentation-client.ts` — reads the UA off the event's request
 * headers (case-insensitive key, matching how proxies/Next normalize
 * headers inconsistently) since there is no `navigator` on the server.
 */
export function isBotEvent(event: ErrorEvent): boolean {
  const headers = event.request?.headers
  if (!headers) return false
  const key = Object.keys(headers).find((k) => k.toLowerCase() === "user-agent")
  if (!key) return false
  const ua = headers[key]
  return typeof ua === "string" && isBotUserAgent(ua)
}
